import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Trash2, MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

interface CommentsTabProps {
  userId: string;
  onCountChange?: (count: number) => void;
}

interface CommentRow {
  id: string;
  content: string;
  created_at: string;
  idea_id: string;
  ideaTitle: string | null;
}

const CommentsTab = ({ userId, onCountChange }: CommentsTabProps) => {
  const [rows, setRows] = useState<CommentRow[] | null>(null);

  const load = useCallback(async () => {
    const { data: comments } = await supabase
      .from("idea_comments")
      .select("id, content, created_at, idea_id")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    const list = comments ?? [];
    const ideaIds = Array.from(new Set(list.map((c) => c.idea_id)));
    let titleMap = new Map<string, string>();
    if (ideaIds.length > 0) {
      const { data: ideas } = await supabase
        .from("ideas")
        .select("id, title")
        .in("id", ideaIds);
      titleMap = new Map((ideas ?? []).map((i) => [i.id, i.title]));
    }
    const enriched: CommentRow[] = list.map((c) => ({
      ...c,
      ideaTitle: titleMap.get(c.idea_id) ?? null,
    }));
    setRows(enriched);
    onCountChange?.(enriched.length);
  }, [userId, onCountChange]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("idea_comments").delete().eq("id", id);
    if (error) {
      toast.error("Could not delete comment");
      return;
    }
    setRows((prev) => {
      const next = prev?.filter((r) => r.id !== id) ?? null;
      if (next) onCountChange?.(next.length);
      return next;
    });
    toast.success("Comment deleted");
  };

  if (rows === null) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-20 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border p-10 text-center">
        <MessageCircle className="h-6 w-6 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          You haven't commented on any projects yet.
        </p>
        <Link to="/ideas">
          <Button size="sm" variant="outline">Browse Projects</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {rows.map((row) => (
        <div
          key={row.id}
          className="rounded-xl border border-border bg-card p-4"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm text-foreground">{row.content}</p>
              <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                <span>
                  on{" "}
                  {row.ideaTitle ? (
                    <Link
                      to="/ideas"
                      className="text-foreground hover:text-primary transition-colors"
                    >
                      {row.ideaTitle}
                    </Link>
                  ) : (
                    <span className="italic">a deleted project</span>
                  )}
                </span>
                <span aria-hidden="true">·</span>
                <span>{formatDistanceToNow(new Date(row.created_at), { addSuffix: true })}</span>
              </div>
            </div>
            <button
              onClick={() => handleDelete(row.id)}
              className="text-muted-foreground hover:text-destructive transition-colors"
              aria-label="Delete comment"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CommentsTab;
